<script setup lang="ts">
    import { computed, inject, onMounted, reactive } from 'vue';
    import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
    import { ContextAccess } from '@shared/Root/Context.access';
    import { FeatureService } from '@shared/directives/Feature.service';
    import ConfirmButton from '@shared/components/ConfirmButton.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { formatDate } from '@shared/utils/formatDateTime';
    import get_gravatar from '@shared/utils/Gravatar.js';
    import List from '@shared/components/List/List.vue';
    import Tabs from '@shared/components/Tabs.vue';
    import Tab from '@shared/components/Tab.vue';
    import Modal from '@shared/components/Modal.vue';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import Loading from '@shared/components/Loading.vue';
    import UserRolePicker from './UserRolePicker.vue';
    import ChangeOwnerModal from './ChangeOwnerModal.vue';
    import InviteUserModal from './InviteUserModal.vue';
    import Groups from '@account/Groups/Groups.vue';
    import Roles from '@account/Roles/Roles.vue';
    import { loadLists } from '@views/Lists/Lists.access';
    import { List as ListType } from '@views/Lists/ListsTypes';
    import {
        getUsers,
        removeUser,
        resetTotpKey,
        getRoles,
        changeOwnerOfTasks,
        changeOwnerOfApiKeys,
        getPendingInvites,
        revokeInvite,
        acceptInvite,
        exportUsersToNewList,
        exportUsersToExistingList,
        exportUsersToExcel,
        setUserPaymentAuthorized,
        UserAccountRole,
        AccountUserInviteStatus,
        type AccountUser,
        type RoleSummary,
        type AccountUserInvite,
    } from './AccountUsers.access';
    import BSDropdownButton from '@shared/components/BSDropdownButton.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle } from '@shared/components/Form/Form';

    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const featureAccess: FeatureService = injectorAccess.injector.get(FeatureService);

    const hasRolesFeature = computed(() => featureAccess.hasFeature('Users.Roles'));
    const hasAclFeature = computed(() => featureAccess.hasFeature('Users.Acl'));
    const hasBackOfficeFeature = computed(() => featureAccess.hasFeature('BackOffice'));

    const state = reactive<{
        loading: boolean;
        users: AccountUser[];
        roles: RoleSummary[];
        invites: AccountUserInvite[];
        busyUserIds: Set<string>;
        busyInviteIds: Set<string>;
        showChangeOwnerOfTasksModal: boolean;
        showChangeOwnerOfApiKeysModal: boolean;
        showInviteUserModal: boolean;
        exportingUsers: boolean;
        showExportToExistingListModal: boolean;
        exportListOptions: ListType[] | null;
        selectedExportListId: string | null;
        pendingValidationCount: number;
        columnVisibility: {
            role: boolean;
            created: boolean;
            lastLogin: boolean;
            activeTask: boolean;
            activeApiKey: boolean;
        };
    }>({
        loading: true,
        users: [],
        roles: [],
        invites: [],
        busyUserIds: new Set(),
        busyInviteIds: new Set(),
        showChangeOwnerOfTasksModal: false,
        showChangeOwnerOfApiKeysModal: false,
        showInviteUserModal: false,
        exportingUsers: false,
        showExportToExistingListModal: false,
        exportListOptions: null,
        selectedExportListId: null,
        pendingValidationCount: 0,
        columnVisibility: {
            role: true,
            created: true,
            lastLogin: true,
            activeTask: false,
            activeApiKey: false,
        },
    });

    const usersWithActiveApiKeys = computed(() => state.users.filter((user) => (user.ActiveApiKeyCount || 0) > 0));

    const hasGoogleAuthenticatorLoginMethod = computed(() => {
        return !!contextAccess.state.currentAccount?.EnabledAuthenticationMethodNames?.includes('PasswordAndGoogleAuthenticator');
    });

    const filterKeys = ['Name', 'Email'];

    const moreActions = [
        { key: 'changeOwnerOfTasks', display: $Resources.AccountUsersChangeOwnerOfTasks },
        { key: 'changeOwnerOfApiKeys', display: $Resources.AccountUsersChangeOwnerOfApiKeys },
    ];

    function onMoreActionClick(key: string): void {
        if (key === 'changeOwnerOfTasks') {
            state.showChangeOwnerOfTasksModal = true;
        } else if (key === 'changeOwnerOfApiKeys') {
            state.showChangeOwnerOfApiKeysModal = true;
        }
    }

    async function onOwnerChanged(): Promise<void> {
        state.users = await getUsers();
    }

    async function onRolesChanged(): Promise<void> {
        const [users, roles] = await Promise.all([getUsers(), getRoles()]);
        state.users = users;
        state.roles = roles;
    }

    const orderOptions = computed(() => [
        { key: 'Email', display: $Resources.EmailLabel },
        { key: 'Name', display: $Resources.NameLabel },
        { key: 'Created', display: $Resources.AccountUsersCreatedLabel },
        { key: 'LastLogin', display: $Resources.AccountUsersLastLogIn },
    ]);

    const usersTableHeaders = computed(() => {
        const headers: (string | { name: string; classes?: string })[] = ['', $Resources.UserProfileNameAndEmailLabel];

        if (state.columnVisibility.role) {
            headers.push($Resources.UserProfileRoleLabel);
            headers.push($Resources.AccountUsersPaymentAuthorizedLabel);
        }

        if (state.columnVisibility.created) {
            headers.push($Resources.AccountUsersCreatedLabel);
        }

        if (state.columnVisibility.lastLogin) {
            headers.push($Resources.AccountUsersLastLogIn);
        }

        if (state.columnVisibility.activeTask) {
            headers.push({ name: $Resources.AccountUsersActiveTaskLabel, classes: 'text-right' });
        }

        if (state.columnVisibility.activeApiKey) {
            headers.push({ name: $Resources.AccountUsersActiveApiKeyLabel, classes: 'text-right' });
        }

        headers.push({ name: $Resources.AccountUsersRemoveLabel, classes: 'text-right' });

        if (hasGoogleAuthenticatorLoginMethod.value) {
            headers.push($Resources.GoogleAuthenticatiorConnectedLabel);
        }

        return headers;
    });

    const inviteTableHeaders = computed(() => {
        const headers = [
            '',
            $Resources.AccountUsersPendingEmailLabel,
            $Resources.AccountUsersPendingStatusLabel,
            $Resources.AccountUsersPendingExpiresLabel,
            $Resources.AccountUsersPendingRoleLabel,
            $Resources.AccountUsersPendingRevokeLabel,
        ];

        if (hasBackOfficeFeature.value) {
            headers.push('Manual accept');
        }

        return headers;
    });

    onMounted(async () => {
        const [users, roles, invites] = await Promise.all([getUsers(), getRoles(), getPendingInvites()]);
        state.users = users;
        state.roles = roles;
        state.invites = invites;
        state.loading = false;
    });

    function isCurrentUser(user: AccountUser): boolean {
        return user.UserId === contextAccess.state.currentUser?.UserId;
    }

    function onRoleChanged(user: AccountUser, roleType: UserAccountRole, customRoleId: string | null): void {
        user.UserAccountRole = roleType;
        user.CustomRoleId = customRoleId;
    }

    // "Betalningsbehörig" (SMS top-up on invoice, R1): may top up the SMS balance. Shown optimistically, reverted on failure.
    async function onPaymentAuthorizedChanged(user: AccountUser, paymentAuthorized: boolean): Promise<void> {
        const before = user.PaymentAuthorized;
        user.PaymentAuthorized = paymentAuthorized;
        state.busyUserIds.add(user.UserId);
        try {
            await setUserPaymentAuthorized(user.UserId, paymentAuthorized);
        } catch {
            user.PaymentAuthorized = before;
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.busyUserIds.delete(user.UserId);
        }
    }

    function isRemoveBlocked(user: AccountUser): boolean {
        return (user.ActiveTask || 0) > 0 || (user.ActiveApiKeyCount || 0) > 0;
    }

    function onRemoveUserBlocked(): void {
        bsAlert({ type: AlertTypes.Danger, title: $Resources.RemoveUserBlockedHeading, content: $Resources.RemoveUserBlockedBody });
    }

    async function onRemoveUser(user: AccountUser): Promise<void> {
        state.busyUserIds.add(user.UserId);

        try {
            await removeUser(user.UserId);

            if (isCurrentUser(user)) {
                window.location.href = '/Account/ChooseAccount';
                return;
            }

            state.users = state.users.filter((u) => u.UserId !== user.UserId);
            bsAlert({ type: AlertTypes.Success });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorRemoveUserHeading, content: $Resources.ErrorRemoveUserBody });
        } finally {
            state.busyUserIds.delete(user.UserId);
        }
    }

    async function onResetTotpKey(user: AccountUser): Promise<void> {
        state.busyUserIds.add(user.UserId);

        try {
            await resetTotpKey(user.UserId);
            user.HasTotpKey = false;
            bsAlert({ type: AlertTypes.Success });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorRemoveUserHeading, content: $Resources.ErrorRemoveUserBody });
        } finally {
            state.busyUserIds.delete(user.UserId);
        }
    }

    function onInvited(invite: AccountUserInvite): void {
        state.invites = [...state.invites, invite];
    }

    async function onExportUsersToExcel(): Promise<void> {
        state.exportingUsers = true;

        try {
            const result = await exportUsersToExcel();
            window.location.href = result.Url;
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.exportingUsers = false;
        }
    }

    async function onExportUsersToNewList(): Promise<void> {
        state.exportingUsers = true;

        try {
            const result = await exportUsersToNewList(state.users);
            window.location.href = `/Lists/${result.ListId}`;
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.exportingUsers = false;
        }
    }

    async function openExportToExistingListModal(): Promise<void> {
        if (!state.exportListOptions) {
            state.exportListOptions = await loadLists();
        }

        state.selectedExportListId = null;
        state.showExportToExistingListModal = true;
    }

    async function confirmExportToExistingList(): Promise<void> {
        if (!state.selectedExportListId) return;

        state.showExportToExistingListModal = false;
        state.exportingUsers = true;

        try {
            const result = await exportUsersToExistingList(state.users, state.selectedExportListId);
            window.location.href = `/Lists/${result.ListId}`;
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.exportingUsers = false;
        }
    }

    function daysToExpire(expireDate: string): number {
        return Math.abs(Math.floor((new Date().getTime() - new Date(expireDate).getTime()) / 86400000));
    }

    function getDeviceId(): string {
        let deviceId = localStorage.getItem('ug_device_id');

        if (!deviceId) {
            deviceId = window.crypto.randomUUID();
            localStorage.setItem('ug_device_id', deviceId);
        }

        return deviceId;
    }

    async function onRevokeInvite(invite: AccountUserInvite): Promise<void> {
        state.busyInviteIds.add(invite.AccountUserInviteId);

        try {
            await revokeInvite(invite.AccountUserInviteId);
            state.invites = state.invites.filter((i) => i.AccountUserInviteId !== invite.AccountUserInviteId);
            bsAlert({
                type: AlertTypes.Success,
                title: $Resources.AccountUserInviteRevokeSuccessHeader,
                content: `${$Resources.AccountUserInviteRevokeSuccessBodyFirst} ${invite.Email} ${$Resources.AccountUserInviteRevokeSuccessBodySecond}`,
            });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.AccountUserInviteRevokeFailedHeader });
        } finally {
            state.busyInviteIds.delete(invite.AccountUserInviteId);
        }
    }

    async function onManuallyAcceptInvite(invite: AccountUserInvite): Promise<void> {
        state.busyInviteIds.add(invite.AccountUserInviteId);

        try {
            await acceptInvite(invite.AccountUserInviteId, {
                FirstName: invite.FirstName,
                LastName: invite.LastName,
                Email: invite.Email,
                Password: `${window.crypto.randomUUID()}-${window.crypto.randomUUID()}`,
                DeviceId: getDeviceId(),
            });

            state.invites = state.invites.filter((i) => i.AccountUserInviteId !== invite.AccountUserInviteId);
            state.users = await getUsers();
            bsAlert({ type: AlertTypes.Success });
        } catch {
            bsAlert({ type: AlertTypes.Danger, title: $Resources.AccountUserInviteSentFailedHeader });
        } finally {
            state.busyInviteIds.delete(invite.AccountUserInviteId);
        }
    }
</script>

<template>
    <h1 class="h2 m-lg-b">{{ $Resources.AccountUsersTab }}</h1>

    <Tabs>
        <Tab :title="$Resources.AccountUsersHeading">
            <h2 class="h3">{{ $Resources.AccountUsersHeading }}</h2>

            <List
                :items="state.users"
                itemId="UserId"
                displayAs="table"
                :tableHeaders="usersTableHeaders"
                :loading="state.loading"
                :filterKeys="filterKeys"
                :orderOptions="orderOptions"
                :moreActions="moreActions"
                orderByValue="Name"
                orderByDirection="asc"
                @moreActionClick="onMoreActionClick">
                <template #toolbar>
                    <button class="btn btn-primary" type="button" @click="state.showInviteUserModal = true">
                        {{ $Resources.AccountUsersAddUser }}
                    </button>

                    <BSDropdownButton menuClass="dropdown-menu-right">
                        <template #toggle>{{ $Resources.ColumnsLabel }}</template>
                        <template #content>
                            <li class="dropdown-menu-content">
                                <h4 class="m-0-t">{{ $Resources.ColumnsLabel }}</h4>

                                <FormCheckable
                                    class="m-sm-b"
                                    :label="$Resources.UserProfileRoleLabel"
                                    :checked="state.columnVisibility.role"
                                    @update:modelValue="(v) => (state.columnVisibility.role = !!v)" />
                                <FormCheckable
                                    class="m-sm-b"
                                    :label="$Resources.AccountUsersCreatedLabel"
                                    :checked="state.columnVisibility.created"
                                    @update:modelValue="(v) => (state.columnVisibility.created = !!v)" />
                                <FormCheckable
                                    class="m-sm-b"
                                    :label="$Resources.AccountUsersLastLogIn"
                                    :checked="state.columnVisibility.lastLogin"
                                    @update:modelValue="(v) => (state.columnVisibility.lastLogin = !!v)" />
                                <FormCheckable
                                    class="m-sm-b"
                                    :label="$Resources.AccountUsersActiveTaskLabel"
                                    :checked="state.columnVisibility.activeTask"
                                    @update:modelValue="(v) => (state.columnVisibility.activeTask = !!v)" />
                                <FormCheckable
                                    :label="$Resources.AccountUsersActiveApiKeyLabel"
                                    :checked="state.columnVisibility.activeApiKey"
                                    @update:modelValue="(v) => (state.columnVisibility.activeApiKey = !!v)" />
                            </li>
                        </template>
                    </BSDropdownButton>

                    <BSDropdownButton menuClass="dropdown-menu-right" :disabled="state.exportingUsers || !state.users.length" closeOnClickInside>
                        <template #toggle>
                            <span class="glyphicon glyphicon-export m-xxs-r"></span>
                            {{ $Resources.ExportButton }}
                        </template>
                        <template #content>
                            <li>
                                <a href="javascript:void(0)" role="button" @click="onExportUsersToExcel">
                                    {{ $Resources.ExportToExcelButton }}
                                </a>
                            </li>
                            <li>
                                <a href="javascript:void(0)" role="button" @click="onExportUsersToNewList">
                                    {{ $Resources.ExportToNewListButton }}
                                </a>
                            </li>
                            <li>
                                <a href="javascript:void(0)" role="button" @click="openExportToExistingListModal">
                                    {{ $Resources.ExportToExistingListButton }}
                                </a>
                            </li>
                        </template>
                    </BSDropdownButton>
                </template>
                <template #item="{ item: user }">
                    <td class="autofit">
                        <span class="profile-avatar d-flex round">
                            <span class="gravatar round" :style="{ 'background-image': 'url(' + get_gravatar(user.Email, 46) + ')' }"></span>
                        </span>
                    </td>
                    <td>
                        <span>{{ user.Name }}</span>
                        <br />
                        <small>{{ user.Email }}</small>
                    </td>
                    <td v-if="state.columnVisibility.role" class="autofit">
                        <UserRolePicker
                            style="width: 180px"
                            :user="user"
                            :roles="state.roles"
                            :disabled="isCurrentUser(user)"
                            @roleChanged="(roleType, customRoleId) => onRoleChanged(user, roleType, customRoleId)"></UserRolePicker>
                    </td>
                    <td v-if="state.columnVisibility.role" class="autofit">
                        <small v-if="user.UserAccountRole === UserAccountRole.Administrator" class="c-light">{{ $Resources.AccountUsersPaymentAuthorizedAlways }}</small>
                        <FormCheckable
                            v-else
                            :label="$Resources.AccountUsersPaymentAuthorizedLabel"
                            :switchStyle="SwitchStyle.Standard"
                            :checked="user.PaymentAuthorized"
                            :disabled="isCurrentUser(user) || state.busyUserIds.has(user.UserId)"
                            @update:modelValue="(v) => onPaymentAuthorizedChanged(user, !!v)" />
                    </td>
                    <td v-if="state.columnVisibility.created">{{ formatDate(user.Created) }}</td>
                    <td v-if="state.columnVisibility.lastLogin">{{ formatDate(user.LastLogin) }}</td>
                    <td v-if="state.columnVisibility.activeTask" class="text-right">
                        {{ user.ActiveTask }}
                    </td>
                    <td v-if="state.columnVisibility.activeApiKey" class="text-right">
                        {{ user.ActiveApiKeyCount || 0 }}
                    </td>
                    <td class="text-right">
                        <button
                            v-if="isRemoveBlocked(user)"
                            type="button"
                            class="btn btn-default btn-xs"
                            :disabled="state.busyUserIds.has(user.UserId)"
                            @click="onRemoveUserBlocked()">
                            {{ $Resources.AccountUsersRemoveLabel }}
                        </button>
                        <ConfirmButton
                            v-else
                            buttonClasses="btn-xs"
                            :buttonText="$Resources.AccountUsersRemoveLabel"
                            :disabled="state.busyUserIds.has(user.UserId)"
                            @confirm="onRemoveUser(user)"
                            undoButton></ConfirmButton>
                    </td>
                    <td class="text-right" v-if="hasGoogleAuthenticatorLoginMethod">
                        <ConfirmButton
                            v-if="user.HasTotpKey"
                            buttonClasses="btn-xs"
                            :buttonText="$Resources.GoogleAuthenticatorRemoveTotpLabel"
                            :disabled="state.busyUserIds.has(user.UserId)"
                            @confirm="onResetTotpKey(user)"
                            undoButton></ConfirmButton>
                    </td>
                </template>
            </List>
        </Tab>
        <Tab :title="$Resources.AccountUsersPendingTitle + ' (' + state.pendingValidationCount + ')'">
            <h2 class="h3">
                {{ $Resources.AccountUsersPendingTitle }}
                <small>{{ state.pendingValidationCount }}</small>
            </h2>

            <List
                :items="state.invites"
                itemId="AccountUserInviteId"
                displayAs="table"
                :tableHeaders="inviteTableHeaders"
                :loading="state.loading"
                hideSearch
                class="th6-text-right th7-text-right"
                @update:filteredItemsCount="(count) => (state.pendingValidationCount = count)">
                <template #toolbar>
                    <button class="btn btn-primary" type="button" @click="state.showInviteUserModal = true">
                        {{ $Resources.AccountUsersAddUser }}
                    </button>
                </template>
                <template #item="{ item: invite }">
                    <td class="autofit">
                        <span class="profile-avatar d-flex round">
                            <span class="gravatar round" :style="{ 'background-image': 'url(' + get_gravatar(invite.Email, 46) + ')' }"></span>
                        </span>
                    </td>
                    <td>{{ invite.Email }}</td>
                    <td>
                        <span v-if="invite.Status === AccountUserInviteStatus.Revoked">{{ $Resources.AccountUsersRevokedStatus }}</span>
                        <span v-else>{{ $Resources.AccountUsersPendingStatus }}</span>
                    </td>
                    <td>{{ daysToExpire(invite.ExpireDate) }} {{ $Resources.AccountUsersExpiresInLabel }}</td>
                    <td>
                        <span v-if="invite.RoleType === UserAccountRole.Administrator">{{ $Resources.AccountUsersPendingRoleAdministrator }}</span>
                        <span v-else-if="invite.RoleType === UserAccountRole.User">{{ $Resources.AccountUsersPendingRoleUser }}</span>
                        <span v-else>{{ invite.RoleName }}</span>
                    </td>
                    <td class="text-right">
                        <ConfirmButton
                            buttonClasses="btn-xs"
                            :buttonText="$Resources.AccountUsersPendingRevokeLabel"
                            :disabled="state.busyInviteIds.has(invite.AccountUserInviteId)"
                            @confirm="onRevokeInvite(invite)"
                            undoButton></ConfirmButton>
                    </td>
                    <td class="text-right" v-if="hasBackOfficeFeature">
                        <button
                            type="button"
                            class="btn btn-default btn-xs"
                            :disabled="state.busyInviteIds.has(invite.AccountUserInviteId)"
                            @click="onManuallyAcceptInvite(invite)">
                            Manual accept
                        </button>
                    </td>
                </template>
            </List>
        </Tab>
        <Tab v-if="hasRolesFeature" :title="$Resources.RolesAndPermissionsHeading">
            <Roles @rolesChanged="onRolesChanged"></Roles>
        </Tab>
        <Tab v-if="hasAclFeature" :title="$Resources.AclGroupHeading">
            <Groups></Groups>
        </Tab>
    </Tabs>

    <ChangeOwnerModal
        :show="state.showChangeOwnerOfTasksModal"
        :title="$Resources.ChangeOwnerOfScheduledTasks"
        :fromUsers="state.users"
        :allUsers="state.users"
        :changeOwner="changeOwnerOfTasks"
        @close="state.showChangeOwnerOfTasksModal = false"
        @changed="onOwnerChanged"></ChangeOwnerModal>

    <ChangeOwnerModal
        :show="state.showChangeOwnerOfApiKeysModal"
        :title="$Resources.ChangeOwnerOfApiKeys"
        :fromUsers="usersWithActiveApiKeys"
        :allUsers="state.users"
        :changeOwner="changeOwnerOfApiKeys"
        @close="state.showChangeOwnerOfApiKeysModal = false"
        @changed="onOwnerChanged"></ChangeOwnerModal>

    <InviteUserModal :show="state.showInviteUserModal" :roles="state.roles" @close="state.showInviteUserModal = false" @invited="onInvited"></InviteUserModal>

    <Modal :show="state.showExportToExistingListModal" :title="$Resources.ExportToExistingListTitle" @close="state.showExportToExistingListModal = false">
        <template #content>
            <SingleSelect
                :items="state.exportListOptions || []"
                :modelValue="state.selectedExportListId"
                selectProp="ListId"
                displayProps="Name"
                :label="$Resources.SelectListLabel"
                @update:modelValue="(val) => (state.selectedExportListId = val)" />
        </template>
        <template #button>
            <button type="button" class="btn btn-primary" :disabled="!state.selectedExportListId || state.exportingUsers" @click="confirmExportToExistingList">
                {{ $Resources.ExportButton }}
            </button>
        </template>
    </Modal>

    <Loading :show="state.exportingUsers" loading-style="progress-bar" fixed></Loading>
</template>
